import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Stethoscope, UserRound, Building2, Briefcase, Sparkles, FileText, Plane, Languages, MapPin, Heart, Activity, Hotel, Video, Handshake } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from '../hooks/useTranslation';
import Logo from './Logo';

export default function PublicHeader({ currentPage = '' }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const services = [
    { name: t('servicesMenu.airportPickup'), path: '/services/airport-pickup', icon: Plane },
    { name: t('servicesMenu.languageInterpreter'), path: '/services/language-interpreter', icon: Languages },
    { name: t('servicesMenu.surroundingTours'), path: '/services/surrounding-tours', icon: MapPin },
    { name: t('servicesMenu.nursingCare'), path: '/services/nursing-care', icon: Heart },
    { name: t('servicesMenu.physiotherapist'), path: '/services/physiotherapist', icon: Activity },
    { name: t('servicesMenu.travelAccommodation'), path: '/services/travel-accommodation', icon: Hotel },
    { name: t('servicesMenu.videoConsultations'), path: '/services/video-consultations', icon: Video },
  ];

  const isActive = (path) => currentPage === path;

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-orange-100 z-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2 lg:gap-3 cursor-pointer flex-shrink-0" onClick={() => navigate('/')}>
          <Logo size="md" className="hidden sm:flex" />
          <Logo size="sm" className="flex sm:hidden" />
          <div>
            <h1 className="font-heading text-xl lg:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
              MedRahi
            </h1>
            <p className="text-xs text-gray-600 hidden sm:block">{t('landing.healthcareWithoutBorders')}</p>
          </div>
        </div>
        
        {/* Desktop Navigation - Only show on lg and above */}
        <nav className="hidden lg:flex items-center gap-3 xl:gap-5">
          <button 
            onClick={() => navigate('/treatments')} 
            className={`text-sm font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${isActive('treatments') ? 'text-orange-600' : 'text-gray-700 hover:text-orange-600'}`}
          >
            <Stethoscope className="w-4 h-4" />
            {t('nav.treatments')}
          </button>
          <button 
            onClick={() => navigate('/doctors')} 
            className={`text-sm font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${isActive('doctors') ? 'text-orange-600' : 'text-gray-700 hover:text-orange-600'}`}
          >
            <UserRound className="w-4 h-4" />
            {t('nav.doctors')}
          </button>
          <button 
            onClick={() => navigate('/hospitals')} 
            className={`text-sm font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${isActive('hospitals') ? 'text-orange-600' : 'text-gray-700 hover:text-orange-600'}`}
          >
            <Building2 className="w-4 h-4" />
            {t('nav.hospitals')}
          </button>
          
          {/* Services Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className={`text-sm font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${isActive('services') ? 'text-orange-600' : 'text-gray-700 hover:text-orange-600'}`}
                data-testid="services-dropdown-trigger"
              >
                <Briefcase className="w-4 h-4" />
                {t('nav.services')}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-56">
              {services.map((service) => {
                const ServiceIcon = service.icon;
                return (
                  <DropdownMenuItem 
                    key={service.path} 
                    onClick={() => navigate(service.path)}
                    className="cursor-pointer hover:bg-orange-50 flex items-center gap-2"
                    data-testid={`service-link-${service.path.split('/').pop()}`}
                  >
                    <ServiceIcon className="w-4 h-4 text-orange-500" />
                    {service.name}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          
          <button 
            onClick={() => navigate('/wellness')} 
            className={`text-sm font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${isActive('wellness') ? 'text-orange-600' : 'text-gray-700 hover:text-orange-600'}`}
          >
            <Sparkles className="w-4 h-4" />
            {t('nav.wellness')}
          </button>
          <button 
            onClick={() => navigate('/medical-visa')} 
            className={`text-sm font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${isActive('medical-visa') ? 'text-orange-600' : 'text-gray-700 hover:text-orange-600'}`}
          >
            <FileText className="w-4 h-4" />
            {t('nav.medicalVisa')}
          </button>
          <button 
            onClick={() => navigate('/partners')} 
            className={`text-sm font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${isActive('partners') ? 'text-orange-600' : 'text-gray-700 hover:text-orange-600'}`}
          >
            <Handshake className="w-4 h-4" />
            Partners
          </button>
        </nav>

        {/* Desktop Auth Buttons - Only show on lg and above */}
        <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-shrink-0">
          <LanguageSelector />
          <button
            onClick={() => navigate('/login')}
            className="px-3 xl:px-4 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap"
            data-testid="header-login-button"
          >
            {t('nav.signIn')}
          </button>
          <button
            onClick={() => navigate('/register')}
            className="px-4 xl:px-6 py-2.5 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all text-sm whitespace-nowrap"
            data-testid="header-register-button"
          >
            {t('nav.getStarted')}
          </button>
        </div>

        {/* Tablet/Mobile: Show key actions + hamburger */}
        <div className="flex lg:hidden items-center gap-2">
          <LanguageSelector />
          <button
            onClick={() => navigate('/login')}
            className="hidden sm:block px-3 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors"
          >
            {t('nav.signIn')}
          </button>
          <button
            className="p-2 rounded-lg hover:bg-orange-50 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed top-20 left-0 right-0 border-t border-orange-100 bg-white z-40 max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
          <nav className="px-4 py-4 space-y-3">
            {/* Language Selector for Mobile - Hidden if already shown in header */}
            <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100 pb-3 sm:hidden">
              <span className="text-sm text-gray-600">Language</span>
              <LanguageSelector />
            </div>
            
            <button
              onClick={() => { navigate('/treatments'); setMobileMenuOpen(false); }}
              className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-3"
            >
              <Stethoscope className="w-5 h-5 text-orange-500" />
              {t('nav.treatments')}
            </button>
            <button
              onClick={() => { navigate('/doctors'); setMobileMenuOpen(false); }}
              className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-3"
            >
              <UserRound className="w-5 h-5 text-orange-500" />
              {t('nav.doctors')}
            </button>
            <button
              onClick={() => { navigate('/hospitals'); setMobileMenuOpen(false); }}
              className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-3"
            >
              <Building2 className="w-5 h-5 text-orange-500" />
              {t('nav.hospitals')}
            </button>
            
            {/* Services Section in Mobile */}
            <div className="px-4 py-2">
              <p className="text-sm font-semibold text-orange-600 mb-2 flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                {t('nav.services')}
              </p>
              <div className="space-y-1 pl-2">
                {services.map((service) => {
                  const ServiceIcon = service.icon;
                  return (
                    <button
                      key={service.path}
                      onClick={() => { navigate(service.path); setMobileMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-2"
                      data-testid={`mobile-service-link-${service.path.split('/').pop()}`}
                    >
                      <ServiceIcon className="w-4 h-4 text-orange-400" />
                      {service.name}
                    </button>
                  );
                })}
              </div>
            </div>
            
            <button
              onClick={() => { navigate('/wellness'); setMobileMenuOpen(false); }}
              className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-3"
            >
              <Sparkles className="w-5 h-5 text-orange-500" />
              {t('nav.wellness')}
            </button>
            <button
              onClick={() => { navigate('/medical-visa'); setMobileMenuOpen(false); }}
              className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-3"
            >
              <FileText className="w-5 h-5 text-orange-500" />
              {t('nav.medicalVisa')}
            </button>
            <button
              onClick={() => { navigate('/partners'); setMobileMenuOpen(false); }}
              className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors flex items-center gap-3"
            >
              <Handshake className="w-5 h-5 text-orange-500" />
              Partners
            </button>
            <div className="pt-3 border-t border-gray-200 space-y-2">
              <button
                onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                className="block w-full text-center px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors font-medium"
              >
                {t('nav.signIn')}
              </button>
              <button
                onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                className="block w-full text-center px-4 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                {t('nav.getStarted')}
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
